// Listing card: links to `/blog/[slug]`; hero image may be CDN-backed.
import Image from 'next/image'
import Link from 'next/link'
import { Clock } from 'lucide-react'

import { BLOG_CATEGORY_BADGE_BG } from '@/features/blog/constants/blog.constants'
import { blogCategoryLabel } from '@/features/blog/constants/blog-filters'
import type { BlogPost, BlogPostCategoryId } from '@/features/blog/types/blog.types'
import { authorInitials } from '@/features/blog/util/author-initials'
import { formatBlogDate } from '@/features/blog/util/format-blog-date'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar'
import { Badge } from '@/shared/components/ui/Badge'
import { Card, CardContent, CardFooter, CardTitle } from '@/shared/components/ui/card'
import { cn } from '@/shared/lib/utils'

const linkRing =
  'block h-full rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-[#001F5F] focus-visible:ring-offset-2'

const cardShell =
  'group h-full gap-0 overflow-hidden rounded-2xl border border-neutral-200/90 bg-white p-0 py-0 shadow-sm transition-shadow hover:shadow-md'

const catBadge =
  'inline-flex min-h-[23.99px] w-fit items-center justify-center rounded-full border-0 px-3 py-1 text-xs font-semibold leading-none text-neutral-900'

export function BlogPostCard({ post, serifHeadingClass }: { post: BlogPost; serifHeadingClass: string }) {
  return (
    <Link href={`/blog/${post.slug}`} className={linkRing}>
      <Card className={cardShell}>
        <div className="relative aspect-[16/10] w-full cursor-pointer overflow-hidden bg-white">
          <Image
            src={post.image}
            alt={post.title}
            fill
            className="object-contain object-center transition-transform duration-300 group-hover:scale-[1.02]"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        </div>
        <CardContent className="flex flex-1 flex-col gap-3 px-6 pb-0 pt-5">
          <Pill categoryId={post.category} />
          <CardTitle className={cn(serifHeadingClass, 'text-lg font-bold leading-snug text-neutral-950 sm:text-xl')}>
            {post.title}
          </CardTitle>
          <p className="line-clamp-3 text-sm leading-relaxed text-neutral-600">{post.excerpt}</p>
        </CardContent>
        <CardFooter className="mt-auto flex items-center gap-2.5 border-t border-neutral-100 px-6 pb-5 pt-4">
          <Avatar size="sm" className="size-9 shrink-0 ring-2 ring-white">
            <AvatarImage src={post.author.avatarUrl} alt={post.author.name} />
            <AvatarFallback className="bg-neutral-200 text-xs font-semibold text-neutral-700">
              {authorInitials(post.author.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-1 items-center gap-x-3 md:gap-x-0">
            <span className="min-w-0 truncate text-sm font-semibold text-neutral-900">{post.author.name}</span>
            <time
              dateTime={post.date}
              className="hidden shrink-0 text-xs text-neutral-500 md:ml-auto md:block md:whitespace-nowrap"
            >
              {formatBlogDate(post.date)}
            </time>
          </div>
          <span className="ml-auto flex shrink-0 items-center gap-1 text-xs text-neutral-500">
            <Clock className="size-3.5 shrink-0 text-neutral-400" aria-hidden />
            <span>{post.readingTime}</span>
          </span>
        </CardFooter>
      </Card>
    </Link>
  )
}

function Pill({ categoryId }: { categoryId: BlogPostCategoryId }) {
  return (
    <Badge variant="warning" className={catBadge} style={{ backgroundColor: BLOG_CATEGORY_BADGE_BG }}>
      {blogCategoryLabel(categoryId)}
    </Badge>
  )
}
